/**
 * useGisMapData
 *
 * Fetches GIS asset data for a location via the /geomap/locations/{id} endpoint,
 * which returns an array of GeoJSON FeatureCollections (Point nodes + LineString links),
 * and parses them into typed GisNode / GisLine arrays ready for DeckGL rendering.
 *
 * Re-fetches automatically when locationId changes.
 * Resets to empty data when locationId is null.
 *
 * NOTE: Flow animation data (TripsLayer) is fetched separately by useFlowMapData
 * using the /links/locations/{id}/minify endpoint.
 */

import { useState, useEffect, useMemo } from 'react';
import { getGeomap } from '@/services/links';
import { parseGisResponse, EMPTY_GIS_DATA } from '@/utils/gisUtils';
import type { GisData } from '@/utils/gisUtils';

export type { GisData } from '@/utils/gisUtils';
export type { GisNode, GisLine } from '@/utils/gisUtils';

export function useGisMapData(locationId: number | null | undefined): GisData {
	const [rawResponse, setRawResponse] = useState<any>(null);
	const [currentLocationId, setCurrentLocationId] = useState<number | null | undefined>(locationId);

	useEffect(() => {
		if (!locationId) {
			setRawResponse(null);
			setCurrentLocationId(null);
			return;
		}

		if (locationId === currentLocationId && rawResponse !== null) return;

		setCurrentLocationId(locationId);
		setRawResponse(null);

		getGeomap(locationId)
			.then((response: any) => {
				setRawResponse(response ?? []);
			})
			.catch((err: any) => {
				console.error('[useGisMapData] Failed to load GIS data:', err);
				setRawResponse([]);
			});
	}, [locationId]);

	return useMemo<GisData>(() => {
		if (!locationId || rawResponse === null) return EMPTY_GIS_DATA;
		return parseGisResponse(rawResponse);
	}, [locationId, rawResponse]);
}

export default useGisMapData;
